import { EIDOutputFormat, FrameType, type ScpFrame } from "./types";
import { normaliseEid } from "./eid";

/**
 * Calls the event handler and catches any exception it throws so the protocol
 * layer can finish cleanly. Errors are logged rather than swallowed because they
 * point to an application bug, not a device problem.
 */
export function safeEmit<TEvent>(
  onEvent: (event: TEvent) => void,
  event: TEvent,
): void {
  try {
    onEvent(event);
  } catch (error) {
    // Isolate consumer failures so protocol cleanup can finish even if the event handler throws.
    // eslint-disable-next-line no-console
    console.error("Unhandled device event handler error", error);
  }
}

/**
 * Interprets a live text frame from the scanner as a valid EID scan or an
 * invalid-EID signal. When EID normalisation returns nothing for a non-empty
 * frame value, onInvalidEid is called rather than silently discarding the frame.
 */
export function processLiveScpFrame(
  frame: ScpFrame,
  eidOutputFormat: EIDOutputFormat | undefined,
  onScan: (eid: string) => void,
  onInvalidEid: () => void,
): void {
  if (frame.type !== FrameType.TEXT) {
    return;
  }

  const eid = normaliseEid(frame.value, eidOutputFormat);
  if (eid) {
    onScan(eid);
    return;
  }

  if (!frame.value) {
    return;
  }

  onInvalidEid();
}
